import i18next from 'i18next';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { Link } from 'react-router-dom';
import { formatCurrency, numberFormat } from 'src/utils';
import type { Quotation } from 'src/quotations/types';
import { noleggiatoreLabel, sourceLabel } from './columnsData';

type Props = {
	quotation: Quotation | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

type Attr = { label: string; value: ReactNode; wide?: boolean; span2?: boolean };

const serviziToAttrs = (servizi: Record<string, unknown> | null): Attr[] =>
	Object.entries(servizi ?? {}).map(([label, value]) =>
		value === '' || value == null
			? { label: '', value: label, wide: true }
			: { label, value: String(value) }
	);

const dash = (v: unknown): ReactNode =>
	v === null || v === undefined || v === '' ? '—' : (v as ReactNode);

const money = (v: number | null | undefined) => (v != null ? formatCurrency(v) : '—');

export default function QuotationDetailOverlay({ quotation, open, onOpenChange }: Props) {
	if (!quotation) return null;

	const m = quotation;

	const quotationAttrs: Attr[] = [
		{ label: i18next.t('quotation_code', { defaultValue: 'Codice preventivo' }), value: m.code, span2: true },
		{ label: i18next.t('quotation_source', { defaultValue: 'Origine' }), value: sourceLabel(m.source) },
		{ label: i18next.t('creation_date', { defaultValue: 'Data di creazione' }), value: dayjs(m.createdAt).format('DD/MM/YYYY HH:mm'), span2: true },
		{ label: i18next.t('quotation_recipient_short', { defaultValue: 'Intestatario' }), value: dash(m.client) },
		{ label: i18next.t('client_type'), value: m.clientType ? i18next.t(m.clientType) : '—' },
		{ label: i18next.t('quotation_created_by', { defaultValue: 'Utente' }), value: dash(m.createdBy) },
		{ label: i18next.t('reference_partner'), value: dash(m.userAffiliation) },
	];

	const vehicleAttrs: Attr[] = [
		{ label: i18next.t('brand'), value: dash(m.makeLabel) },
		{ label: i18next.t('model'), value: dash(m.modelLabel) },
		{ label: i18next.t('car_setup'), value: dash(m.trimLabel) },
	];

	if (m.stockVehicleId) {
		vehicleAttrs.push({
			label: i18next.t('vehicle_id'),
			value: <Link to={`/details/${m.stockVehicleId}`}>{m.stockVehicleId}</Link>,
		});
	}

	if (m.vehicleDealer) {
		vehicleAttrs.push({
			label: i18next.t('dealer'),
			value: m.vehicleDealer?.replace('_', ' ')
		})
	}

	if (m.quoteRequestId) {
		vehicleAttrs.push({
			label: i18next.t('quote_request_id', { defaultValue: 'ID richiesta quotazione' }),
			value: m.quoteRequestId,
		});
	}

	const offerAttrs: Attr[] = [
		{ label: i18next.t('rental_company'), value: dash(noleggiatoreLabel(m)) },
		{ label: i18next.t('rental_fee'), value: money(m.canone) },
		{ label: i18next.t('duration'), value: m.durata != null ? `${m.durata} ${i18next.t('months')}` : '—' },
		{ label: i18next.t('total_km'), value: m.kmTotali != null ? numberFormat(m.kmTotali) : '—' },
		{ label: i18next.t('downpayment', { defaultValue: 'Anticipo' }), value: money(m.anticipo) },
	];

	const section = (attrs: Attr[], title?: string) =>
		attrs.length === 0 ? null : (
			<section className="quotation-detail-section">
				{title && <h6 className="quotation-detail-heading">{title}</h6>}
				<div className="quotation-detail-grid">
					{attrs.map((attr, idx) => (
						<div key={`${attr.label}-${idx}`} className={attr.wide ? 'is-wide' : attr.span2 ? 'is-span2' : undefined}>
							{attr.label && <div className="quotation-detail-label">{attr.label}</div>}
							<div className="quotation-detail-value">{attr.value}</div>
						</div>
					))}
				</div>
			</section>
		);

	return (
		<Offcanvas className="quotation-detail w-75" show={open} onHide={() => onOpenChange(false)} placement="end">
			<Offcanvas.Header closeButton>
				<Offcanvas.Title>
					{i18next.t('quotation_detail_title', { defaultValue: 'Dettaglio preventivo' })}
				</Offcanvas.Title>
			</Offcanvas.Header>
			<Offcanvas.Body>
				{section(quotationAttrs)}
				{section(vehicleAttrs)}
				{section(offerAttrs)}
				{section(serviziToAttrs(m.servizi))}

				{m.optionals.length > 0 && (
					<section className="quotation-detail-section">
						<h6 className="quotation-detail-heading">{i18next.t('optionals')}</h6>
						<div className="quotation-detail-optionals">
							{m.optionals.map((opt, idx) => (
								<div key={idx} className="quotation-detail-optional">
									<span>{dash(opt.descrizione)}</span>
									<span className="quotation-detail-value">{money(opt.valore)}</span>
								</div>
							))}
						</div>
					</section>
				)}
			</Offcanvas.Body>
		</Offcanvas>
	);
}
